// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  OpenInNew,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
  FileCopy as CopyIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedIcon,
  AccessTime as AccessTimeIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentTypes } from '../model/props';
import { toSentence } from '../util/string';
import { formatHHMMSS } from '../util/date';

const maxSize = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);
const appName = process.env.REACT_APP_NAME;
const storeId = process.env.REACT_APP_PAYPHONE_STORE_ID;
const apiToken = process.env.REACT_APP_PAYPHONE_API_TOKEN;

const payphoneButtonId = 'pp-button';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900'],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  bankCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(2),
  },
  bankInfo: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  bankLogo: {
    width: 48,
    height: 48,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: theme.shape.borderRadius,
    },
  },
  uploadArea: {
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[100],
    },
  },
  stripeContainer: {
    background: 'linear-gradient(135deg, #635bff 0%, #4f46e5 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  stripeContent: {
    position: 'relative',
    zIndex: 1,
  },
  stripeLogo: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  priceDisplay: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: `${theme.spacing(2)}px 0`,
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  securityBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    marginTop: theme.spacing(2),
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },

  deunaContainer: {
    background: 'linear-gradient(135deg, #522785 0%, #8666a9 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  paymentProcessedButton: {
    width: '100%',
    maxWidth: 360,
    height: 52,
    background: '#522785',
    color: '#ffff',
    borderRadius: 24,
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    margin: '5px 5px 5px 5px',
    boxShadow: 'none',
    border: 'none',

    '&:hover': {
      background: '#3f2956a6',
    },

    '&:active': {
      transform: 'scale(0.98)',
    },

    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  },

  deunaButton: {
    width: '100%',
    maxWidth: 360,
    height: 52,
    background: '#f4f1ff',
    color: '#5b2a84', //
    borderRadius: 24,
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    margin: '5px 5px 5px 5px',
    boxShadow: 'none',
    border: 'none',

    '&:hover': {
      background: '#e8e1ff',
    },

    '&:active': {
      transform: 'scale(0.98)',
      // boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    },

    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  },
  payphoneContainer: {
    background: 'linear-gradient(135deg, #ff5a1f 0%, #e9531dff 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  payphoneContent: {
    position: 'relative',
    zIndex: 1,
  },
  payphoneLogo: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  verificationCard: {
    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  validationCard: {
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  rejectedCard: {
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(244, 67, 54, 0.2)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  verificationContent: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  verificationIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
    animation: (status) => (status === 'verifying' ? '$pulse 2s infinite' : 'none'),
  },
  verificationTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  verificationDescription: {
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  verificationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: theme.shape.borderRadius * 2,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
  },

  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.8,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  fadeInAnimation: {
    animation: '$fadeInUp 0.6s ease-out',
  },
}));
const baseUrl = process.env.PUBLIC_URL || '';
const ProcessRenewalPaymentData = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    code,
    process,
    expanded,
    loading,
    disabled: propsDisabled,
    onExpanded,
    onSave,
    priceCode,
    onRefreshProcess,
    refreshLoading,
  } = props;
  const [isExpired, setIsExpired] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState('');
  const { transferData, hasPayValidated, proofOfPaymentName } = process || {};
  const [mode, setMode] = useState(process?.paymentType || '');
  const [coupon] = useState(process?.coupon || '');
  const [file, setFile] = useState();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentData, setPaymentData] = useState();
  const [tiposdepago, setTiposdepago] = useState([]);

  const expiresAt = paymentData?.linkData?.expiresAt;

  React.useEffect(() => {
    const FIVE_HOURS_MS = 1 * 60 * 60 * 1000;

    if (!expiresAt) return;

    const expirationTime = new Date(expiresAt).getTime();

    // cycleRemaining starts at 5 hours when real remaining > 5 hours
    let cycleRemaining = FIVE_HOURS_MS;

    const interval = setInterval(() => {
      const now = Date.now();
      const realDiff = expirationTime - now;

      if (realDiff <= 0) {
        setTimeLeft('00:00:00');
        setIsExpired(true);
        clearInterval(interval);
        return;
      }

      setIsExpired(false);

      if (realDiff <= FIVE_HOURS_MS) {
        // show actual remaining time
        setTimeLeft(formatHHMMSS(realDiff));
      } else {
        // show a 5-hour repeating countdown to encourage payment
        // decrement cycleRemaining; when it hits 0 reset to 5 hours
        cycleRemaining = cycleRemaining - 1000;
        if (cycleRemaining <= 0) {
          cycleRemaining = FIVE_HOURS_MS;
        }
        setTimeLeft(formatHHMMSS(cycleRemaining));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Auto-refresh process data every 5 minutes (300,000 ms)
  React.useEffect(() => {
    if (!onRefreshProcess || typeof onRefreshProcess !== 'function') return;

    const refreshInterval = setInterval(() => {
      onRefreshProcess();
    }, 1 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [onRefreshProcess]);

  // Early return if no process
  if (!process) {
    return (
      <Accordion
        key={code}
        expanded={expanded}
        onChange={onExpanded && onExpanded(code)}
        TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${code}-panel`}
          id={`${code}-panel`}
          className={classes.accordionSummary}>
          <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PaymentIcon /> {t('Payment data')}
          </Typography>
          <Chip
            icon={<EditIcon />}
            label={t('Not loaded')}
            color="default"
            size="small"
            className={classes.statusChip}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="textSecondary">
            {t('Payment data is not available')}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  // Reset payment data when priceCode changes to force refresh
  useEffect(() => {
    if (priceCode) {
      setPaymentData(null); // Clear payment data to force reload
      setPaymentLoading(false);
    }
  }, [priceCode]);

  // Update mode when process changes
  useEffect(() => {
    if (process && process.paymentType && mode !== process.paymentType) {
      setMode(process.paymentType);
    }
  }, [process, mode]);

  // Función para copiar al portapapeles
  const copyToClipboard = (text, messageKey = 'Copied to clipboard') => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        enqueueSnackbar(t(messageKey), {
          variant: 'success',
        });
      })
      .catch(() => {
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        enqueueSnackbar(t(messageKey), {
          variant: 'success',
        });
      });
  };

  // Función para obtener el logo/icono del banco
  const getBankLogo = (bankName, bankCode) => {
    const name = bankName?.toLowerCase() || '';
    const code = bankCode?.toLowerCase() || '';

    // Intenta primero con imagen real, si no existe usa emoji como fallback
    const bankImages = {
      pichincha: `${baseUrl}/images/banks/pichincha.png`,
      guayaquil: `${baseUrl}/images/banks/guayaquil.png`,
      pacifico: `${baseUrl}/images/banks/pacifico.png`,
    };

    if (name.includes('pacífico')) {
      return { type: 'image', src: bankImages.pacifico };
    }

    // Busca por nombre del banco
    for (const [key, imagePath] of Object.entries(bankImages)) {
      if (name.includes(key) || code.includes(key)) {
        return { type: 'image', src: imagePath };
      }
    }

    // Fallback a emojis si no hay imagen
    if (name.includes('pichincha') || code.includes('pichincha')) {
      return { type: 'emoji', src: '🏦' };
    } else if (name.includes('guayaquil') || code.includes('guayaquil')) {
      return { type: 'emoji', src: '🏛️' };
    } else if (name.includes('pacífico') || code.includes('pacifico')) {
      return { type: 'emoji', src: '🌊' };
    } else {
      // Logo genérico basado en la primera letra del nombre
      return { type: 'text', src: bankName?.charAt(0)?.toUpperCase() || 'B' };
    }
  };

  // Función para obtener el color del banco
  const getBankColor = (bankName) => {
    const name = bankName?.toLowerCase() || '';

    if (name.includes('pichincha')) {
      return '#FFD700'; // Dorado
    } else if (name.includes('guayaquil')) {
      return '#8B4513'; // Azul
    } else if (name.includes('pacífico')) {
      return '#00BFFF'; // Azul cielo
    } else {
      return '#6B73FF'; // Azul por defecto
    }
  };

  //@react-hooks/exhaustive-deps
  const loadPayment = useCallback(() => {
    if (
      (!propsDisabled &&
        (mode.toLowerCase() === PaymentTypes.CREDIT_CARD.toLowerCase() ||
          mode.toLowerCase() === PaymentTypes.PAYPHONE.toLowerCase())) ||
      (mode.toLowerCase() === PaymentTypes.DEUNA.toLowerCase() &&
        (!paymentData || !!paymentData.error || mode.toLowerCase() !== paymentData.vendor.toLowerCase()))
    ) {
      setPaymentLoading(true);
      if (process && process.getPaymentData) {
        process
          .getPaymentData(mode.toLowerCase())
          .then((data) => {
            setPaymentData(data);
            setPaymentLoading(false);
          })
          .catch(() => {
            setPaymentLoading(false);
          });
      } else {
        setPaymentLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, process, propsDisabled]); // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!process || !process.paymentTypes) {
      setTiposdepago([PaymentTypes.TRANSFER]); // Default payment type for renewal
      return;
    }
    const types = [...process.paymentTypes];
    if (types.includes(PaymentTypes.CREDIT_CARD) && !types.includes(PaymentTypes.PAYPHONE)) {
      types.push(PaymentTypes.PAYPHONE);
    }
    if (!types.includes(PaymentTypes.DEUNA)) {
      types.unshift(PaymentTypes.DEUNA);
    }
    setTiposdepago(types);
  }, [process]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  useEffect(() => {
    if (
      !!paymentData &&
      !!paymentData.scripts &&
      paymentData.scripts.length > 0 &&
      paymentData.vendor === 'CREDIT_CARD'
    ) {
      const scripts = paymentData.scripts.map((url) => {
        const script = document.createElement('script');
        if (url.indexOf('http') === 0) {
          script.src = url;
          script.async = true;
        } else {
          script.innerText = url;
        }
        document.body.appendChild(script);
        return script;
      });
      return () => {
        for (let script of scripts) {
          if (!!document && !!document.body) {
            document.body.removeChild(script);
          }
        }
      };
    } else if (!!paymentData && !paymentData.error && paymentData.vendor === 'PAYPHONE' && !!paymentData.info) {
      // Clear existing PayPhone button before creating new one
      const payphoneElement = document.getElementById(payphoneButtonId);
      if (payphoneElement) {
        payphoneElement.innerHTML = '';
      }

      const redirectClientTraxID = Date.now();
      const refer = `Pago ${appName} ${t(toSentence(process.processType || 'Renewal'))}, ${
        process.durationValue ? process.durationValue() : process.daysDuration || 1
      } ${t(process.durationLabel ? process.durationLabel() : 'year').toLowerCase()}`;

      if (window.PPaymentButtonBox) {
        new window.PPaymentButtonBox({
          token: apiToken,
          clientTransactionId: paymentData.info.transactionId + '-' + redirectClientTraxID,
          amount: paymentData.info.total,
          amountWithTax: paymentData.info.subtotalWithTax,
          amountWithoutTax: paymentData.info.subtotalWithoutTax,
          tax: paymentData.info.tax,
          currency: 'USD',
          storeId: storeId,
          reference: refer,
          defaultMethod: 'card',
        }).render(`#${payphoneButtonId}`);
      }
    }
  }, [paymentData, process, t, priceCode]); // Added priceCode to dependencies

  // Check if process exists and has required methods/properties
  const hasValidProcess = process && typeof process === 'object';
  const processHasPaymentLoaded =
    hasValidProcess && process.hasPaymentLoaded && typeof process.hasPaymentLoaded === 'function';
  // Check if payment is completed (has nextProcessCode)
  const isPaymentCompleted = hasValidProcess && !!process.nextProcessCode && process.hasPaymentProcess;

  // Default process state if methods don't exist
  const paymentLoaded = processHasPaymentLoaded ? process.hasPaymentLoaded() : false;

  // Use process.state for user feedback states
  const processState = hasValidProcess && process.state ? process.state : 'none';

  let processStatusText = paymentLoaded ? 'Completed' : 'Not completed';
  let processStatusIcon = paymentLoaded ? <DoneIcon /> : <EditIcon />;
  let statusColor = paymentLoaded ? 'primary' : 'default';

  // If payment is completed, override status
  if (isPaymentCompleted) {
    processStatusText = 'Payment Completed';
    processStatusIcon = <CheckCircleIcon />;
    statusColor = 'primary';
  } else {
    // Handle process state-based feedback - add more specific states
    processStatusText = 'Not Completed';
    processStatusIcon = <EditIcon />;
    statusColor = 'default';
  }

  // Adjust disabled state - disable everything if payment is completed
  const disabled = propsDisabled || isPaymentCompleted || (hasPayValidated && processState !== 'VENDOR_REJECTED');

  const onUpload = () => {
    onSave({
      file,
      mode,
      coupon,
    }).then((ok) => {
      if (ok) {
        setFile(null);
      }
    });
  };

  const isTransfer = mode && mode.toLowerCase() === PaymentTypes.TRANSFER.toLowerCase() && !isPaymentCompleted;
  const isCredit = mode && mode.toLowerCase() === PaymentTypes.CREDIT.toLowerCase() && !isPaymentCompleted;
  const isCreditCard = mode && mode.toLowerCase() === PaymentTypes.CREDIT_CARD.toLowerCase() && !isPaymentCompleted;
  const isPayPhone = mode && mode.toLowerCase() === PaymentTypes.PAYPHONE.toLowerCase() && !isPaymentCompleted;
  const isDeuna = mode && mode.toLowerCase() === PaymentTypes.DEUNA.toLowerCase() && !isPaymentCompleted;

  const datafastForm = () => {
    return (
      <form
        action={paymentData.callbackUrl}
        className="paymentWidgets"
        data-brands="AMEX DINERS DISCOVER MAESTRO MASTER VISA"></form>
    );
  };

  const amountAndSecuredBanner = (showSecuredBanner = true) => {
    return (
      <>
        {/* Mostrar el monto a pagar */}
        <div className={classes.priceDisplay}>
          <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 4 }}>
            ${process.price.toFixed(2)} USD
          </Typography>
          <Typography variant="body2" style={{ opacity: 0.8 }}>
            {t('Total amount to pay')}
          </Typography>
        </div>

        {/* Badge de seguridad */}
        {showSecuredBanner && (
          <div className={classes.securityBadge}>
            <SecurityIcon fontSize="small" />
            <Typography variant="caption">{t('Secure payments in Ecuador')}</Typography>
            <CheckCircleIcon fontSize="small" />
          </div>
        )}
      </>
    );
  };

  return (
    <Accordion key={code} expanded={expanded} onChange={onExpanded(code)} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
        className={classes.accordionSummary}>
        <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PaymentIcon /> {t('Payment data')}
        </Typography>
        <Chip
          icon={processStatusIcon}
          label={t(processStatusText)}
          color={statusColor}
          size="small"
          className={classes.statusChip}
        />
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {!isPaymentCompleted && (
            <>
              <Grid item xs={12} sm={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  disabled={disabled || paymentLoading || !!proofOfPaymentName || !!hasPayValidated}>
                  <InputLabel id="paymentTypeLabel">{t('Payment type')}</InputLabel>
                  <Select
                    labelId="paymentTypeLabel"
                    label={t('Payment type')}
                    id="paymentType"
                    name="paymentType"
                    value={mode}
                    displayEmpty
                    onChange={(e) => {
                      if (e && e.target) {
                        setMode(e.target.value);
                      }
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        {isCreditCard ? (
                          <CreditCardIcon color="action" />
                        ) : isTransfer ? (
                          <BankIcon color="action" />
                        ) : (
                          <PaymentIcon color="action" />
                        )}
                      </InputAdornment>
                    }>
                    <MenuItem value="" disabled>
                      <em>{t('Select payment method')}</em>
                    </MenuItem>
                    {tiposdepago.map((item) => (
                      <MenuItem key={item} value={item}>
                        {t(item)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Payment status feedback - show when nextProcessCode exists */}
          {isPaymentCompleted && (
            <Grid item xs={12} className={classes.fadeInAnimation}>
              <div className={classes.validationCard}>
                <div className={classes.verificationContent}>
                  <VerifiedIcon className={classes.verificationIcon} />
                  <Typography variant="h5" className={classes.verificationTitle}>
                    {t('Payment Successfully Completed')}
                  </Typography>
                  <Typography variant="body1" className={classes.verificationDescription}>
                    {t('Your renewal payment has been processed and verified successfully')}
                  </Typography>
                  <div className={classes.verificationBadge}>
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {t('Completed')}
                    </Typography>
                  </div>
                </div>
              </div>
            </Grid>
          )}

          {/* Show transfer info only if payment is NOT completed */}
          {isTransfer && !!transferData && !isPaymentCompleted && !hasPayValidated && processState !== 'approved' && (
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" className={classes.sectionTitle}>
                <BankIcon /> {t('Transfer Information')}
              </Typography>

              <Card className={classes.bankCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <BankIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    {t('Bank Details')}
                  </Typography>
                  <Grid container spacing={2}>
                    {transferData.banks.map((bank) => (
                      <Grid item xs={12} sm={6} key={bank.code}>
                        <Paper className={classes.bankInfo}>
                          <div className={classes.bankLogo} style={{ backgroundColor: getBankColor(bank.name) + '20' }}>
                            {(() => {
                              const logo = getBankLogo(bank.name, bank.code);
                              if (logo.type === 'image') {
                                return (
                                  <img
                                    src={logo.src}
                                    alt={`${bank.name} logo`}
                                    onError={(e) => {
                                      // Si la imagen no se puede cargar, muestra el fallback
                                      e.target.style.display = 'none';
                                      e.target.parentNode.innerHTML = bank.name?.charAt(0)?.toUpperCase() || 'B';
                                    }}
                                  />
                                );
                              } else if (logo.type === 'emoji') {
                                return logo.src;
                              } else {
                                return logo.src;
                              }
                            })()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <Typography variant="subtitle1">
                              <strong>{bank.name}</strong>
                            </Typography>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Typography variant="body2" color="textSecondary">
                                {t('Bank Code')}: <strong>{bank.code}</strong>
                              </Typography>
                              <Tooltip title={t('Copy bank code')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToClipboard(bank.code, 'Bank code copied to clipboard');
                                  }}
                                  style={{
                                    padding: 4,
                                    marginLeft: 4,
                                  }}>
                                  <CopyIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          <BankIcon
                            style={{
                              color: getBankColor(bank.name),
                              fontSize: '2rem',
                              opacity: 0.7,
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              <Paper className={classes.sectionPaper} elevation={0}>
                <Typography variant="subtitle2" gutterBottom color="textSecondary">
                  {t('Transfer Details')}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={t('Target to')} secondary={transferData.target} />
                    <Tooltip title={t('Copy target name')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(transferData.target, 'Target name copied to clipboard');
                        }}
                        style={{ padding: 8 }}>
                        <CopyIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={t('Legal ID')} secondary={transferData.legalID} />
                    <Tooltip title={t('Copy legal ID')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(transferData.legalID, 'Legal ID copied to clipboard');
                        }}
                        style={{ padding: 8 }}>
                        <CopyIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={t('Email')} secondary={transferData.email} />
                    <Tooltip title={t('Copy email')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(transferData.email, 'Email copied to clipboard');
                        }}
                        style={{ padding: 8 }}>
                        <CopyIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={t('Phone')} secondary={transferData.phone} />
                    <Tooltip title={t('Copy phone number')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(transferData.phone, 'Phone number copied to clipboard');
                        }}
                        style={{ padding: 8 }}>
                        <CopyIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AddressIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={t('Address')} secondary={transferData.address} />
                    <Tooltip title={t('Copy address')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(transferData.address, 'Address copied to clipboard');
                        }}
                        style={{ padding: 8 }}>
                        <CopyIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          )}

          {/* Show upload only if payment is NOT completed */}
          {isTransfer &&
            !isPaymentCompleted &&
            (processState === 'rejected' ||
              (!hasPayValidated && processState !== 'approved' && processState !== 'pending')) && (
              <Grid item xs={12}>
                <Paper className={classes.sectionPaper} elevation={0}>
                  <Typography variant="subtitle2" gutterBottom color="textSecondary">
                    {t(`Upload proof of payment`)}
                  </Typography>
                  <Paper className={classes.uploadArea}>
                    <input
                      accept=".pdf,.png,.jpg"
                      className={classes.upload}
                      id="proof-of-payment"
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length) {
                          const file = e.target.files[0];
                          if (file.size >= maxSize) {
                            enqueueSnackbar(
                              t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }),
                              {
                                variant: 'error',
                              }
                            );
                          } else {
                            setFile(file);
                          }
                        }
                      }}
                    />
                    <label htmlFor="proof-of-payment" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                      {!file ? (
                        <>
                          <UploadIcon color="action" style={{ fontSize: 48, marginBottom: 8 }} />
                          <Typography variant="subtitle2" gutterBottom>
                            {t(`Upload proof of payment`)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {t('Click to select file')} (.pdf, .png, .jpg)
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('Proof of payment')}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {file.name}
                          </Typography>
                          <Grid container spacing={1} justifyContent="center">
                            <Grid item>
                              <Button
                                disabled={disabled || paymentLoading}
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={loading ? <CircularProgress color="inherit" size="1rem" /> : <UploadIcon />}
                                onClick={(e) => {
                                  e.preventDefault();
                                  onUpload();
                                }}>
                                {t('Upload')}
                              </Button>
                            </Grid>
                            <Grid item>
                              <IconButton
                                disabled={disabled || paymentLoading}
                                aria-label="delete"
                                className={classes.error}
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFile(null);
                                }}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </label>
                  </Paper>
                </Paper>
              </Grid>
            )}

          {/* Show credit card only if payment is NOT completed */}
          {isCreditCard && !isPaymentCompleted && !hasPayValidated && processState !== 'approved' && (
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" className={classes.sectionTitle}>
                <CreditCardIcon /> {t('Credit Card Payment')}
              </Typography>

              {/* Container principal de Stripe */}
              <div className={classes.stripeContainer}>
                <div className={classes.stripeContent}>
                  <div className={classes.stripeLogo}>
                    <img
                      src={`${baseUrl}/images/banks/stripe.png`}
                      alt={`Stripe logo`}
                      width={35}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = 'S';
                      }}
                    />
                    <Typography variant="h5" component="span" style={{ fontWeight: 'bold', marginLeft: 8 }}>
                      Stripe
                    </Typography>
                  </div>

                  <Typography variant="body1" style={{ marginBottom: 16, opacity: 0.9 }}>
                    {t('Secure payment processing powered by Stripe')}
                  </Typography>

                  {/* Mostrar el monto a pagar */}
                  <div className={classes.priceDisplay}>
                    <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      ${process ? process.price.toFixed(2) : '0.00'} USD
                    </Typography>
                    <Typography variant="body2" style={{ opacity: 0.8 }}>
                      {t('Total amount to pay')}
                    </Typography>
                  </div>

                  {/* Badge de seguridad */}
                  <div className={classes.securityBadge}>
                    <SecurityIcon fontSize="small" />
                    <Typography variant="caption">{t('Secured by 256-bit SSL encryption')}</Typography>
                    <CheckCircleIcon fontSize="small" />
                  </div>
                </div>
              </div>

              <Paper className={classes.sectionPaper} elevation={0}>
                {paymentLoading && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress color="primary" style={{ marginBottom: 16 }} />
                    <Typography variant="body2" color="textSecondary">
                      {t('Loading secure payment link...')}
                    </Typography>
                  </div>
                )}

                {!!paymentData && !paymentLoading && !!paymentData.error && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IconButton aria-label="reload" onClick={loadPayment} style={{ marginBottom: 8 }}>
                      <RefreshIcon className={classes.error} style={{ fontSize: '2rem' }} />
                    </IconButton>
                    <Typography variant="body2" className={classes.error}>
                      {t(paymentData.error)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                      {t('Click the refresh button to try again')}
                    </Typography>
                  </div>
                )}

                {!paymentData && !paymentLoading && !disabled && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      {t('Click below to generate your secure payment link')}
                    </Typography>
                    <Button
                      onClick={loadPayment}
                      disabled={disabled || paymentLoading}
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<RefreshIcon />}>
                      {t('Generate Payment Link')}
                    </Button>
                  </div>
                )}

                {!!paymentData &&
                  !paymentLoading &&
                  !paymentData.error &&
                  paymentData.vendor === 'DATAFAST' &&
                  datafastForm()}

                {!!paymentData && !paymentLoading && !paymentData.error && !!paymentData.linkData && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Typography variant="h6" style={{ marginBottom: 8, color: '#635bff' }}>
                      {t('Ready to pay!')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                      {t("You will be redirected to Stripe's secure payment page to complete your transaction safely")}
                    </Typography>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Grid item>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 16px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: 8,
                            border: '1px solid #e9ecef',
                          }}>
                          <MoneyIcon color="primary" />
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            ${process ? process.price.toFixed(2) : '0.00'} USD
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item>
                        <Button
                          href={paymentData.linkData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<CreditCardIcon />}
                          endIcon={<OpenInNew />}
                          style={{
                            background: 'linear-gradient(135deg, #635bff 0%, #4f46e5 100%)',
                            boxShadow: '0 4px 15px rgba(99, 91, 255, 0.3)',
                            padding: '12px 24px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                          }}>
                          {t('Pay with Stripe')}
                        </Button>
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 16 }}>
                      <SecurityIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                      {t('Your payment information is protected by industry-standard security')}
                    </Typography>
                  </div>
                )}
                <div id={payphoneButtonId}></div>
              </Paper>
            </Grid>
          )}

          {isDeuna && (
            <Grid item xs={12}>
              <Divider style={{ margin: '10px 0' }} />
              <Typography variant="h6" className={classes.sectionTitle}>
                {t('Payment with Deuna')}
              </Typography>
              <div className={classes.deunaContainer}>
                <div className={classes.payphoneLogo}>
                  <img
                    src={`https://www.deuna.ec/assets/img/icon/icon_deuna_mini.png`}
                    alt={`pyphone logo`}
                    width={35}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = 'P';
                    }}
                  />
                  <Typography variant="h5" component="span" style={{ fontWeight: 'bold', marginLeft: 8 }}>
                    Deuna
                  </Typography>
                </div>

                {amountAndSecuredBanner()}

                <div>
                  <Typography variant="h6" style={{ marginBottom: 15, marginTop: 15 }}>
                    {t('Scan the QR code with the Deuna app to complete your payment')}
                  </Typography>
                </div>

                {paymentLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress color="primary" style={{ marginBottom: 16 }} />
                  </div>
                ) : (
                  <>
                    <div>
                      <Typography
                        variant="h4"
                        style={{
                          marginTop: 10,
                          marginBottom: 10,
                          color: isExpired ? 'red' : '#c7b3b2ff',
                          fontWeight: 'bold',
                          alignItems: 'center',
                          gap: 8,
                        }}>
                        {isExpired ? (
                          'El link ha expirado'
                        ) : (
                          <div>
                            <AccessTimeIcon style={{ fontSize: 28, marginRight: 8, marginBottom: 8 }} />
                            {timeLeft}
                          </div>
                        )}
                      </Typography>
                    </div>
                    <div>
                      <img
                        src={paymentData?.linkData?.qr}
                        alt="QR DEUNA"
                        width={250}
                        style={{ borderRadius: 12 }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = 'Error al cargar el QR de Deuna, inténtelo de nuevo';
                        }}
                      />
                    </div>
                    <div>
                      <Typography variant="h6" style={{ marginBottom: 15, marginTop: 15 }}>
                        {t('Press the button to open the Deuna app and complete your payment')}
                      </Typography>
                    </div>
                    <button
                      className={classes.paymentProcessedButton}
                      style={{
                        opacity: isExpired ? 0.5 : 1,
                        cursor: isExpired || refreshLoading ? 'not-allowed' : 'pointer',
                      }}
                      disabled={isExpired || !!refreshLoading}
                      onClick={() => {
                        if (onRefreshProcess && typeof onRefreshProcess === 'function') {
                          onRefreshProcess();
                        }
                      }}>
                      {t('YA HE REALIZADO EL PAGO')}
                    </button>
                    <button
                      className={classes.deunaButton}
                      disabled={isExpired}
                      style={{
                        opacity: isExpired ? 0.5 : 1,
                        cursor: isExpired ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => {
                        if (isExpired) return;
                        if (!paymentData?.linkData?.url) return;
                        window.open(paymentData.linkData.url, '_blank');
                      }}>
                      {isExpired ? 'Link Expirado' : 'IR A DEUNA'}
                    </button>
                  </>
                )}
              </div>
            </Grid>
          )}

          {/* Show payphone only if payment is NOT completed */}
          {isPayPhone && !isPaymentCompleted && processState !== 'approved' && (
            <Grid item xs={12}>
              <Divider style={{ margin: '10px 0' }} />
              <Typography variant="h6" className={classes.sectionTitle}>
                <PhoneIcon /> {t('PayPhone Payment')}
              </Typography>

              {amountAndSecuredBanner()}

              {/* Container principal de PayPhone */}
              <div className={classes.payphoneContainer}>
                <div className={classes.payphoneContent}>
                  <div className={classes.payphoneLogo}>
                    <img
                      src={`${baseUrl}/images/banks/pyphone.png`}
                      alt={`pyphone logo`}
                      width={35}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = 'P';
                      }}
                    />
                    <Typography variant="h5" component="span" style={{ fontWeight: 'bold', marginLeft: 8 }}>
                      PayPhone
                    </Typography>
                  </div>

                  <Typography variant="body1" style={{ marginBottom: 16, opacity: 0.9 }}>
                    {t('Fast and secure mobile payment solution for Ecuador')}
                  </Typography>

                  {/* Mostrar el monto a pagar */}
                  <div className={classes.priceDisplay}>
                    <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      ${process ? process.price.toFixed(2) : '0.00'} USD
                    </Typography>
                    <Typography variant="body2" style={{ opacity: 0.8 }}>
                      {t('Total amount to pay')}
                    </Typography>
                  </div>

                  {/* Badge de seguridad */}
                  <div className={classes.securityBadge}>
                    <SecurityIcon fontSize="small" />
                    <Typography variant="caption">{t('Secure payments in Ecuador')}</Typography>
                    <CheckCircleIcon fontSize="small" />
                  </div>
                </div>
              </div>

              <Paper className={classes.sectionPaper} elevation={0}>
                {paymentLoading && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress color="primary" style={{ marginBottom: 16 }} />
                    <Typography variant="body2" color="textSecondary">
                      {t('Loading PayPhone payment option...')}
                    </Typography>
                  </div>
                )}

                {!!paymentData && !paymentLoading && !!paymentData.error && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IconButton aria-label="reload" onClick={loadPayment} style={{ marginBottom: 8 }}>
                      <RefreshIcon className={classes.error} style={{ fontSize: '2rem' }} />
                    </IconButton>
                    <Typography variant="body2" className={classes.error}>
                      {t(paymentData.error)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                      {t('Click the refresh button to try again')}
                    </Typography>
                  </div>
                )}

                {!paymentData && !paymentLoading && !disabled && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      {t('Click below to generate your PayPhone payment option')}
                    </Typography>
                    <Button
                      onClick={loadPayment}
                      disabled={disabled || paymentLoading}
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<RefreshIcon />}>
                      {t('Generate PayPhone Payment')}
                    </Button>
                  </div>
                )}

                {!!paymentData &&
                  !paymentLoading &&
                  !paymentData.error &&
                  paymentData.vendor === 'PAYPHONE' &&
                  !!paymentData.info && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Typography variant="h6" style={{ marginBottom: 8, color: '#ff5a1f' }}>
                        {t('Ready to pay with PayPhone!')}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginBottom: 16 }}>
                        <PhoneIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                        {t('Payment processed securely through PayPhone Ecuador')}
                      </Typography>
                    </div>
                  )}

                {/* PayPhone button container */}
                <div
                  id={payphoneButtonId}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '20px',
                    minHeight: paymentData?.vendor === 'PAYPHONE' ? '60px' : '0px',
                  }}></div>
              </Paper>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
      {isCredit && <Divider />}
      {isCredit && (
        <AccordionActions>
          <Button
            disabled={disabled || paymentLoading}
            variant="contained"
            color="primary"
            className={classes.button}
            aria-label="save"
            onClick={onUpload}>
            {t(`Save`)}
          </Button>
        </AccordionActions>
      )}
    </Accordion>
  );
};

export default ProcessRenewalPaymentData;
